// The account menu: the organisation (when branding names it) and the role, never a name (a Principal carries no display
// name for the interface; contracts section 8), and sign-out. Built on the surface's Popover, styled
// as the kit's menu (mockups/kit/surface.css .wp-menu).
import type { Principal } from "@waypoint/contracts";
import { Button, Icon, Popover, PopoverContent, PopoverTrigger } from "@waypoint/surface";
import { useState } from "react";
import { rolesInOrder } from "../auth/session-state.js";
import { useSignOut } from "../auth/session.js";
import { t } from "../i18n/t.js";
import { useOrgName } from "../theme/theme-gate.js";

function roleLabel(role: Principal["roles"][number]): string {
  return t(`role.${role}`);
}

export function AccountMenu({ principal }: { principal: Principal }) {
  const signOut = useSignOut();
  const org = useOrgName();
  const [busy, setBusy] = useState(false);
  const roles = rolesInOrder(principal);
  const primary = roleLabel(roles[0] ?? principal.roles[0] ?? "associate");

  const onSignOut = () => {
    setBusy(true);
    signOut().catch(() => {
      // The session is still the API's: stay signed in and let the user try again.
      setBusy(false);
    });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="quiet"
          aria-label={
            org === undefined
              ? t("account.menuNoOrg", { role: primary })
              : t("account.menu", { org, role: primary })
          }
          icon={<Icon name="chevron" size={14} />}
          className="flex-row-reverse"
        >
          <span className="max-w-48 truncate">{primary}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end">
        <dl className="grid gap-2 px-2.5 pt-1.5 pb-2.5">
          {org === undefined ? null : (
            <div className="grid gap-0.5">
              <dt className="text-11 font-semibold tracking-widest text-wp-text-3 uppercase">
                {t("account.organisation")}
              </dt>
              <dd className="truncate text-wp-text">{org}</dd>
            </div>
          )}
          <div className="grid gap-0.5">
            <dt className="text-11 font-semibold tracking-widest text-wp-text-3 uppercase">
              {t("account.roles")}
            </dt>
            {roles.map((role) => (
              <dd key={role} className="text-wp-text">
                {roleLabel(role)}
              </dd>
            ))}
          </div>
        </dl>
        <div className="mx-1 my-1.5 h-px bg-wp-line" role="separator" />
        <Button
          variant="quiet"
          className="w-full justify-start"
          disabled={busy}
          onClick={onSignOut}
        >
          {t("account.signOut")}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
